import type { BackingCurrency } from '../currency';

/**
 * Dropdown item shape returned by fiat-rail registry methods.
 * Label is generic so consumers preserve the narrow union (e.g. `'ACH' | 'SEPA'`)
 * derived from the config.
 */
export interface FiatRailDropdownItem<T extends string, L extends string = string> {
  label: L;
  value: T;
  /**
   * Set when the rail was surfaced despite failing `canAccess` — only possible
   * for `showWhenLocked` rails requested with `includeLocked`. Consumers must
   * render these as unselectable; the developer lacks the access to use them.
   */
  locked?: boolean;
}

/**
 * Region grouping for fiat rails
 */
export const REGION = {
  Eur: 'eur',
  Other: 'other',
  Us: 'us',
} as const;

export type Region = (typeof REGION)[keyof typeof REGION];

/**
 * Fiat rail configuration — single source of truth for a fiat payment rail.
 *
 * `sessionFlag` is an opaque tag: the registry never interprets it directly,
 * it just passes the config to the caller-provided `canAccess` callback.
 *
 * @template F - Union of valid session-flag strings. Pin to a typed union
 *   (e.g. a consuming app's `SessionFlag`) to get compile-time enforcement
 *   that every `sessionFlag` in the config is a member of that union.
 *   Defaults to `string` when flag validation is not needed.
 */
export interface FiatRailConfig<F extends string = string> {
  /** Display label (e.g., 'ACH', 'Wire') */
  label: string;
  /** Associated fiat currency */
  currency: BackingCurrency;
  /** Region grouping */
  region: Region;
  /** Session-gating tag — read only by the canAccess callback, not by the registry itself */
  sessionFlag?: F | null;
  /**
   * Opt in to being listed as `locked` rather than filtered out when the
   * session fails `sessionFlag`, for callers that pass `includeLocked`. Use for
   * rails whose customer-level endorsement is strictly narrower than the
   * developer-level flag, so a locked row can never become wrongly selectable.
   */
  showWhenLocked?: boolean;
}

/**
 * Fiat rail registry interface — methods for accessing fiat rail data.
 *
 * @template T - Rail ID union (e.g., 'ach' | 'sepa' | 'wire')
 * @template L - Label union derived from the config (e.g., 'ACH' | 'SEPA' | 'Wire')
 * @template S - Session type used for access gating
 */
export interface FiatRailRegistry<T extends string, L extends string = string, S = unknown> {
  // Session-agnostic
  getLabel(rail: string): L;
  getCurrency(rail: T): BackingCurrency;
  getRegion(rail: T): Region;
  getAllRails(): T[];
  getConfig(rail: T): FiatRailConfig | undefined;

  // Session-aware — S is the caller's session type
  canAccess(rail: T, session: S | null): boolean;
  getDropdownItems(session: S | null, options?: { includeLocked?: boolean }): FiatRailDropdownItem<T, L>[];
}
